import type { SchutzOptimierenAttribut, SchutzOptimierenKatalog } from '@comparit/common-api';
import type { FormControl, FormRecord, NonNullableFormBuilder } from '@angular/forms';
import type { Observable } from 'rxjs';
import { BehaviorSubject, map, startWith } from 'rxjs';

/**
 * What the form reads from a "Schutz optimieren" catalogue. Structural on purpose — every Sach
 * app's generated `SchutzOptimierenKatalog` satisfies it, and this lib must not name one of them.
 */
export type SchutzOptimierenKatalogInput = Pick<SchutzOptimierenKatalog, 'kacheln'> & {
  /**
   * `dataType` stays widened to `string` rather than taking the generated `AttributDataType`:
   * an unknown member then renders as plain text instead of breaking the build.
   */
  attribute: (Pick<SchutzOptimierenAttribut, 'id' | 'technischerName'> & { dataType: string })[];
};

export type SchutzOptimierenKachelInput = SchutzOptimierenKatalogInput['kacheln'][number];

/** The persisted form value: one on/off per attribute, keyed by technischerName. */
export type SchutzOptimierenValue = Record<string, boolean>;

/** Kacheln without a sort go last, in catalogue order. */
const bySort = (a: SchutzOptimierenKachelInput, b: SchutzOptimierenKachelInput) =>
  (a.sort ?? Number.MAX_SAFE_INTEGER) - (b.sort ?? Number.MAX_SAFE_INTEGER);

/**
 * The "Schutz optimieren" selection of a Sach funnel. The catalogue decides which controls exist;
 * a Kachel is a named bundle of attribute ids and is "selected" when all of its attributes are on.
 *
 * `TDataType` is the app's generated `AttributDataType` enum. It is passed in rather than written
 * here so the emitted `dynamischeAngaben` assign straight into the generated request DTO.
 */
export class SchutzOptimierenForm<TDataType extends string> {
  constructor(
    private fb: NonNullableFormBuilder,
    private booleanDataType: TDataType,
    private defaultValue?: SchutzOptimierenValue | null
  ) {}

  /** Empty until `setKatalog` runs. Never invalid — choosing nothing is a valid answer. */
  readonly fg: FormRecord<FormControl<boolean>> = this.fb.record<boolean>({});

  private attributNameById = new Map<string, string>();
  private attributIdByName = new Map<string, string>();

  private readonly kachelnSubject = new BehaviorSubject<SchutzOptimierenKachelInput[]>([]);
  readonly kacheln$ = this.kachelnSubject.asObservable();

  readonly fgValueChanges$: Observable<SchutzOptimierenValue> = this.fg.valueChanges.pipe(
    map(() => this.fg.getRawValue())
  );

  readonly fgValue$: Observable<SchutzOptimierenValue> = this.fg.valueChanges.pipe(
    startWith(null),
    map(() => this.fg.getRawValue())
  );

  /**
   * The selection as the backend wants it: only attributes that are on, each with its catalogue id.
   * Shaped like `SchutzOptimierenAngaben.dynamischeAngaben` of the generated client.
   */
  readonly dynamischeAngaben$: Observable<
    Record<string, { tarifAttributeId: string; type: { dataType: TDataType; value: boolean } }>
  > = this.fgValue$.pipe(map(value => this.toDynamischeAngaben(value)));

  /**
   * Rebuilds the controls from a catalogue: one per Boolean attribute, keyed by technischerName.
   * Persisted values survive for names still in the catalogue; everything else is dropped.
   */
  setKatalog(katalog: SchutzOptimierenKatalogInput): void {
    const attribute = katalog.attribute.filter(a => a.dataType === this.booleanDataType);
    this.attributNameById = new Map(attribute.map(a => [a.id, a.technischerName]));
    this.attributIdByName = new Map(attribute.map(a => [a.technischerName, a.id]));

    const names = new Set(attribute.map(a => a.technischerName));
    Object.keys(this.fg.controls)
      .filter(name => !names.has(name))
      .forEach(name => this.fg.removeControl(name, { emitEvent: false }));

    attribute
      .filter(a => !this.fg.contains(a.technischerName))
      .forEach(a =>
        this.fg.addControl(
          a.technischerName,
          this.fb.control<boolean>(this.defaultValue?.[a.technischerName] ?? false),
          { emitEvent: false }
        )
      );

    // One emission for the whole rebuild, not one per control.
    this.fg.updateValueAndValidity();
    this.kachelnSubject.next([...katalog.kacheln].sort(bySort));
  }

  /** A Kachel counts as selected when every attribute it names that we know of is on. */
  isKachelSelected(kachel: SchutzOptimierenKachelInput): boolean {
    const names = this.controlNamesOf(kachel);
    return names.length > 0 && names.every(name => this.fg.controls[name].value);
  }

  toggleKachel(kachel: SchutzOptimierenKachelInput): void {
    const names = this.controlNamesOf(kachel);
    if (names.length === 0) return;

    const next = !this.isKachelSelected(kachel);
    this.fg.patchValue(Object.fromEntries(names.map(name => [name, next])));
  }

  reset(): void {
    this.fg.patchValue(
      Object.fromEntries(Object.keys(this.fg.controls).map(name => [name, false]))
    );
  }

  /** Attribute ids the catalogue filtered out (non-Boolean) have no control and are skipped. */
  private controlNamesOf(kachel: SchutzOptimierenKachelInput): string[] {
    return kachel.attributIds
      .map(id => this.attributNameById.get(id))
      .filter((name): name is string => name !== undefined && this.fg.contains(name));
  }

  private toDynamischeAngaben(value: SchutzOptimierenValue) {
    const result: Record<
      string,
      { tarifAttributeId: string; type: { dataType: TDataType; value: boolean } }
    > = {};

    for (const [name, selected] of Object.entries(value)) {
      const tarifAttributeId = this.attributIdByName.get(name);
      if (!selected || !tarifAttributeId) continue;
      result[name] = { tarifAttributeId, type: { dataType: this.booleanDataType, value: true } };
    }

    return result;
  }
}
